import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as Icon from 'react-feather';

import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';

import { getWhatsappReport } from '../../../store/slices/tenantSlices/chatSlice';
import WhatsappReportInfo from './WhatsappReportInfo';
import PreviewModal from '../../Chat/PreviewModal';
import { whatsappListGet } from '../../../store/slices/tenantSlices/configurationSlice';
import FormInput from '../../../components/form/FormInput';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { formatPayloadDate } from '../../../utils/common';

const WhatsappReport = () => {
  const dispatch = useDispatch();

  const timezone = useTimezone();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [whatsappOptions, setWhatsappOptions] = useState([]);
  const [infoDetails, setInfoDetails] = useState(null);
  const [isPreviewUrl, setIsPreviewUrl] = useState(null);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    whatsapp_number: '',
    to_number: ''
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
    // whatsapp_number: Yup.string().required('Whatsapp number is required'),
    // to_number: Yup.string().required('to number is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values,
        to_number: values?.to_number ? `91${values?.to_number}@c.us` : undefined,
        whatsapp_number: values?.whatsapp_number || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(getWhatsappReport(payload)).unwrap();
      if (response?.data) {
        setReportData({
          data: response.data || [],
          header: response.header || []
        });
      } else {
        setReportData({
          data: [],
          header: []
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getWhatsappOption = async () => {
    try {
      const response = await dispatch(whatsappListGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.whatsapp_number || '-',
          value: `${val.whatsapp_number}@c.us`
        }));
        setWhatsappOptions(options);
      } else {
        setWhatsappOptions([]);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getWhatsappOption();
  }, []);

  return (
    <div>
      <AdminHeader
        title="Whatsapp Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  maxDate={today}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  maxDate={today}
                  minDate={values.start_date}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Whatsapp Number"
                  placeholder="Select Whatsapp Number"
                  value={values.whatsapp_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="whatsapp_number"
                  options={whatsappOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  name="to_number"
                  label="To Number"
                  placeholder="Enter To Number"
                  value={values.to_number}
                  onChange={(e) => {
                    const value = e.target.value;
                    const val = value.replace(/[^0-9*#]+/g, '');
                    setFieldValue('to_number', val);
                  }}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                  <th className="text-center" style={{ width: '90px' }}>
                    Info
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                      <td>
                        <div className="d-flex justify-content-center align-items-center">
                          <Icon.Info
                            size={16}
                            className="cursor-pointer text-primary"
                            onClick={() =>
                              setInfoDetails({
                                ...val,
                                to_userId: values?.to_userId,
                                type: values?.type
                              })
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 330px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}
      {infoDetails && (
        <WhatsappReportInfo
          isOpen={!!infoDetails}
          handleCancel={() => setInfoDetails(null)}
          details={infoDetails}
          setIsPreviewUrl={setIsPreviewUrl}
        />
      )}
      {isPreviewUrl && (
        <PreviewModal
          isOpen={isPreviewUrl}
          url={isPreviewUrl}
          handleCancel={() => setIsPreviewUrl(null)}
        />
      )}
    </div>
  );
};

export default WhatsappReport;
